import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {Popover} from 'shells/popover/popover';
import {Rating} from 'shells/rating/rating';
import {StatusBadge} from 'shells/status-badge/status-badge';

import type {
    GetPeepEngagementStats as GetPeepEngagementStatsQuery,
    GetPeepEngagementStatsVariables as GetPeepEngagementStatsQueryVariables,
    GetPeepEngagementStats_peep as EngagementStats,
} from 'nutshell-graphql-types';

import GET_PEEP_ENGAGEMENT_STATS from './graphql/queries/get-peep-engagement-stats.graphql';
import {PeepEmStats} from './peep-em-stats';

import styles from './peep-em-rating.module.css';

type OwnProps = {
    peepId: string;
    hasUnsubscribedFromAllAudiences: boolean | null | undefined;
};

type Props = {
    stats: EngagementStats;
    hasUnsubscribedFromAllAudiences: boolean | null | undefined;
};

function PeepEmRatingComponent(props: Props) {
    const [containerRef, setContainerRef] = React.useState<HTMLElement | null | undefined>(null);
    const [popoverOpen, setPopoverOpen] = React.useState<boolean>(false);

    const renderStatusBadge = () => {
        if (props.hasUnsubscribedFromAllAudiences) {
            return (
                <div className={styles['badge-outer-container']}>
                    <div className={styles['badge-inner-container']}>
                        <StatusBadge
                            level='warning'
                            text='Unsubscribed'
                            detail={
                                props.stats.name
                                    ? `${props.stats.name} has opted out of all marketing communications`
                                    : undefined
                            }
                        />
                    </div>
                </div>
            );
        }

        // @ts-expect-error
        if (props.stats.hasHardBounced) {
            return (
                <div className={styles['badge-outer-container']}>
                    <div className={styles['badge-inner-container']}>
                        <StatusBadge
                            level='alert'
                            text='Bouncing'
                            detail={
                                props.stats.name
                                    ? `Your emails to ${props.stats.name} are bouncing`
                                    : undefined
                            }
                        />
                    </div>
                </div>
            );
        }
    };

    return (
        <React.Fragment>
            <div
                ref={setContainerRef}
                onMouseEnter={() => setPopoverOpen(true)}
                onMouseLeave={() => setPopoverOpen(false)}
                className={styles.container}
            >
                <Rating score={props.stats.engagementRating} />
            </div>
            {popoverOpen ? (
                <Popover anchor={containerRef} location='bottom-right'>
                    <div className={styles['popover-content-container']}>
                        <div>
                            This rating is calculated using the open, click, and reply rates, as
                            well as the total number of emails sent from Nutshell that include open
                            or click tracking
                        </div>
                        <div className={styles['popover-stats-container']}>
                            <PeepEmStats {...props.stats.engagementStats} />
                        </div>
                    </div>
                </Popover>
            ) : undefined}
            {renderStatusBadge()}
        </React.Fragment>
    );
}

export function PeepEmRating(props: OwnProps) {
    const {data} = useQuery<GetPeepEngagementStatsQuery, GetPeepEngagementStatsQueryVariables>(
        GET_PEEP_ENGAGEMENT_STATS,
        {
            variables: {id: props.peepId},
            notifyOnNetworkStatusChange: true,
        }
    );

    const peep = data ? data.peep : null;

    if (peep) {
        return (
            <PeepEmRatingComponent
                stats={peep}
                hasUnsubscribedFromAllAudiences={props.hasUnsubscribedFromAllAudiences}
            />
        );
    }

    return null;
}
